import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Axe, Menu, X, ShieldCheck, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/curriculum", label: "Curriculum" },
  { to: "/workbooks", label: "Workbooks" },
  { to: "/pricing", label: "Enroll" },
  { to: "/tutor", label: "AI Tutor" },
  { to: "/services", label: "Services" },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <span className="flex h-10 w-10 items-center justify-center border border-primary bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
            <Axe className="h-5 w-5" strokeWidth={2.5} />
          </span>
          <span className="leading-none">
            <span className="block font-head text-lg font-800 font-bold tracking-tight text-foreground">HORNSBY</span>
            <span className="label-caps block text-[0.6rem] text-muted-foreground">Homeschool Curriculum</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/curriculum"}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `label-caps px-4 py-2 transition-colors ${isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin">
                  <Button variant="outline" className="rounded-none border-secondary text-secondary-foreground hover:bg-secondary">
                    <ShieldCheck className="mr-2 h-4 w-4" /> Admin
                  </Button>
                </Link>
              )}
              <Link to="/dashboard" data-testid="nav-dashboard">
                <Button variant="outline" className="rounded-none">Members Area</Button>
              </Link>
              <Button onClick={doLogout} variant="ghost" className="rounded-none" data-testid="nav-logout">
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login">
                <Button variant="ghost" className="rounded-none label-caps">Log In</Button>
              </Link>
              <Link to="/register" data-testid="nav-register">
                <Button className="rounded-none label-caps bg-primary hover:bg-primary/80">Get Started</Button>
              </Link>
            </>
          )}
        </div>

        <button className="lg:hidden text-foreground" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="border-t border-border bg-card px-5 py-4 lg:hidden" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="block py-3 label-caps text-muted-foreground">{l.label}</Link>
          ))}
          <div className="mt-3 flex flex-col gap-2">
            {user ? (
              <>
                {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)}><Button variant="outline" className="w-full rounded-none">Admin</Button></Link>}
                <Link to="/dashboard" onClick={() => setOpen(false)}><Button variant="outline" className="w-full rounded-none">Members Area</Button></Link>
                <Button onClick={doLogout} className="w-full rounded-none">Log Out</Button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)}><Button variant="outline" className="w-full rounded-none">Log In</Button></Link>
                <Link to="/register" onClick={() => setOpen(false)}><Button className="w-full rounded-none">Get Started</Button></Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
